import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import ffmpegCore from "@ffmpeg/core?url";

let ffmpegPromise = null;

const getFfmpeg = async () => {
  if (!ffmpegPromise) {
    const ffmpeg = new FFmpeg();
    ffmpegPromise = (async () => {
      await ffmpeg.load({ coreURL: ffmpegCore });
      return ffmpeg;
    })();
  }
  return ffmpegPromise;
};

const safeUnlink = async (ffmpeg, name) => {
  try {
    await ffmpeg.deleteFile(name);
  } catch (error) {
    // ignore
  }
};

/**
 * Stitch video segments into a single video.
 * First attempts fast codec-copy concat. If that fails (e.g. mismatched codecs,
 * resolutions, or frame-rates), falls back to re-encoding with optional
 * crossfade transitions between segments.
 */
export async function stitchVideoSegments(
  segmentUrls = [],
  options = {},
) {
  if (!Array.isArray(segmentUrls) || segmentUrls.length === 0) {
    throw new Error("No video segments to stitch");
  }

  if (segmentUrls.length === 1) {
    const data = await fetchFile(segmentUrls[0]);
    const blob = new Blob([data.buffer], { type: "video/mp4" });
    return { url: URL.createObjectURL(blob), blob };
  }

  const ffmpeg = await getFfmpeg();
  const inputNames = [];
  const tempFiles = [];

  try {
    for (let i = 0; i < segmentUrls.length; i += 1) {
      const name = `segment-${i}.mp4`;
      const data = await fetchFile(segmentUrls[i]);
      await ffmpeg.writeFile(name, data);
      inputNames.push(name);
      tempFiles.push(name);
    }

    const outputName = "stitched.mp4";
    tempFiles.push(outputName);

    const crossfadeSec = Number(options.crossfadeSec) || 0;
    const useTransitions = crossfadeSec > 0 && segmentUrls.length > 1;

    if (useTransitions) {
      return await stitchWithCrossfade(
        ffmpeg,
        inputNames,
        outputName,
        crossfadeSec,
        tempFiles,
      );
    }

    try {
      return await stitchConcatCopy(ffmpeg, inputNames, outputName, tempFiles);
    } catch (copyError) {
      return await stitchReencode(ffmpeg, inputNames, outputName, tempFiles);
    }
  } finally {
    await Promise.all(tempFiles.map((name) => safeUnlink(ffmpeg, name)));
  }
}

async function stitchConcatCopy(ffmpeg, inputNames, outputName, tempFiles) {
  const concatList = inputNames.map((name) => `file ${name}`).join("\n");
  const listName = "segments.txt";
  tempFiles.push(listName);
  await ffmpeg.writeFile(listName, new TextEncoder().encode(concatList));

  await ffmpeg.exec([
    "-f", "concat",
    "-safe", "0",
    "-i", listName,
    "-c", "copy",
    outputName,
  ]);

  const output = await ffmpeg.readFile(outputName);
  if (!output || output.length === 0) {
    throw new Error("Concat-copy produced empty output");
  }
  const blob = new Blob([output.buffer], { type: "video/mp4" });
  return { url: URL.createObjectURL(blob), blob };
}

async function stitchReencode(ffmpeg, inputNames, outputName, tempFiles) {
  const args = [];
  for (const name of inputNames) {
    args.push("-i", name);
  }

  const filterParts = inputNames.map(
    (_, i) => `[${i}:v:0]scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=30[v${i}]`,
  );
  const audioScaleParts = inputNames.map(
    (_, i) => `[${i}:a:0]aresample=44100[a${i}]`,
  );
  const videoConcat = inputNames.map((_, i) => `[v${i}]`).join("");
  const audioConcat = inputNames.map((_, i) => `[a${i}]`).join("");
  const n = inputNames.length;

  let filterComplex =
    filterParts.join(";") +
    ";" +
    `${videoConcat}concat=n=${n}:v=1:a=0[outv]`;

  const hasAudio = true;
  if (hasAudio) {
    filterComplex +=
      ";" +
      audioScaleParts.join(";") +
      ";" +
      `${audioConcat}concat=n=${n}:v=0:a=1[outa]`;
  }

  const execArgs = [
    ...args,
    "-filter_complex", filterComplex,
    "-map", "[outv]",
    ...(hasAudio ? ["-map", "[outa]"] : []),
    "-c:v", "libx264",
    "-preset", "fast",
    "-crf", "23",
    ...(hasAudio ? ["-c:a", "aac", "-b:a", "128k"] : []),
    "-movflags", "+faststart",
    "-y",
    outputName,
  ];

  try {
    await ffmpeg.exec(execArgs);
  } catch (err) {
    const videoOnly = [
      ...args,
      "-filter_complex",
      filterParts.join(";") + ";" + `${videoConcat}concat=n=${n}:v=1:a=0[outv]`,
      "-map", "[outv]",
      "-c:v", "libx264",
      "-preset", "fast",
      "-crf", "23",
      "-an",
      "-movflags", "+faststart",
      "-y",
      outputName,
    ];
    await ffmpeg.exec(videoOnly);
  }

  const output = await ffmpeg.readFile(outputName);
  if (!output || output.length === 0) {
    throw new Error("Re-encode produced empty output");
  }
  const blob = new Blob([output.buffer], { type: "video/mp4" });
  return { url: URL.createObjectURL(blob), blob };
}

async function stitchWithCrossfade(
  ffmpeg,
  inputNames,
  outputName,
  crossfadeSec,
  tempFiles,
) {
  const args = [];
  for (const name of inputNames) {
    args.push("-i", name);
  }

  const n = inputNames.length;
  const cf = Math.max(0.2, Math.min(crossfadeSec, 2));

  const scaleParts = inputNames.map(
    (_, i) => `[${i}:v:0]scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=30[v${i}]`,
  );

  let filterComplex = scaleParts.join(";");

  if (n === 2) {
    filterComplex += `;[v0][v1]xfade=transition=fade:duration=${cf}:offset=0[outv]`;
  } else {
    let prevLabel = "v0";
    for (let i = 1; i < n; i += 1) {
      const outLabel = i === n - 1 ? "outv" : `xf${i}`;
      filterComplex += `;[${prevLabel}][v${i}]xfade=transition=fade:duration=${cf}:offset=0[${outLabel}]`;
      prevLabel = outLabel;
    }
  }

  const execArgs = [
    ...args,
    "-filter_complex", filterComplex,
    "-map", "[outv]",
    "-c:v", "libx264",
    "-preset", "fast",
    "-crf", "23",
    "-an",
    "-movflags", "+faststart",
    "-y",
    outputName,
  ];

  try {
    await ffmpeg.exec(execArgs);
  } catch (err) {
    return await stitchReencode(ffmpeg, inputNames, outputName, tempFiles);
  }

  const output = await ffmpeg.readFile(outputName);
  if (!output || output.length === 0) {
    return await stitchReencode(ffmpeg, inputNames, outputName, tempFiles);
  }
  const blob = new Blob([output.buffer], { type: "video/mp4" });
  return { url: URL.createObjectURL(blob), blob };
}
